import React, { useEffect, useState } from 'react';
import { FaAnglesLeft, FaAnglesRight } from 'react-icons/fa6';
import { NavLink, useLocation } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  APPConfigAction,
  getSelectedShopifyShopID,
  selectCurrentBusinessMode,
} from '../../app/Reducers/AppConfig';
import { EBusinessType } from '../../app/Reducers/AppConfig/types';
import useOutsideClick from '../../hooks/useOutsideClick';
import ShopifyService from '../../utils/Services/Shopify';
import { toastifyGeneralTheme } from '../../utils/ToastifyGeneral';
import Dropdown, { DropdownItemInterface } from '../Navbar/Dropdown';
import { dropshipping_menus, extraMenus, pod_menu } from './config/menus';
import './index.css';

interface CustomSideElementInterface {
  title: string;
  href: string;
  isActive?: boolean;
  iconPath: string;
  isOpen: boolean;
  isActiveBgColor?: string;
  isActiveTextColor?: string;
}
export interface IShopifyStore {
  _id: string;
  shop: string;
  businessType: EBusinessType;
}

const CustomSideElement = ({
  title,
  href,
  isActive = false,
  iconPath,
  isOpen,
  isActiveBgColor = '[#6839ee]/20',
  isActiveTextColor = '#6839ee',
}: CustomSideElementInterface) => {
  return (
    <NavLink
      className={`flex w-full flex-col justify-start gap-2.5 p-2
      text-[#79757f]  
      ${
        isActive
          ? `rounded-lg bg-${isActiveBgColor} text-${isActiveTextColor}`
          : `hover:text-${isActiveTextColor} `
      }
      ${isOpen ? 'md:items-start' : ' items-center'}

      `}
      style={{ color: isActive ? isActiveTextColor : '#79757f' }}
      to={href}
    >
      <div
        className={`inline-flex h-6 items-center justify-center 
         gap-4 md:justify-start ${isOpen ? 'md:ml-2 ' : ''}`}
      >
        <img alt="" className="" src={iconPath}></img>

        <div className={`text-16 hidden  ${isOpen ? 'md:block' : ' '}`}>
          {title}
        </div>
      </div>
    </NavLink>
  );
};
const shopifyService = new ShopifyService();

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [shopifyStores, setShopifyStores] = useState<IShopifyStore[]>([]);
  const [selectedShop, setSelectedShop] = useState<IShopifyStore | null>(null);
  const [isShopDropdownVisible, setIsShopDropdownVisible] = useState(false);
  const shopDropdownRef = useOutsideClick(() =>
    setIsShopDropdownVisible(false)
  );
  const dispatch = useDispatch();
  const currentBusinessType = useSelector(selectCurrentBusinessMode);
  const selectedShopifyShopId = useSelector(getSelectedShopifyShopID);
  const currentPath = useLocation().pathname;
  const location = useLocation();

  // If the stored shop doesn't exist, select the first shop available.
  useEffect(() => {
    if (!currentBusinessType || location.pathname === '/packages') return;
    shopifyService
      .get_data(`/myShops?businessType=${currentBusinessType}`)
      .then((res: IShopifyStore[]) => {
        setShopifyStores(res);

        let selected: IShopifyStore | null = null;
        if (selectedShopifyShopId) {
          const foundShop = res.find(
            shop => shop._id === selectedShopifyShopId
          );
          if (foundShop) {
            selected = foundShop;
          }
        }
        // If no valid stored shop is found, select the first shop (if available)
        if (!selected && res.length > 0) {
          selected = res[0];
          dispatch(APPConfigAction.setSelectedShopifyShopID(selected._id));
        }
        setSelectedShop(selected);
      })
      .catch(err =>
        toast.error('Failed to get the shops!', { ...toastifyGeneralTheme })
      );
  }, [currentBusinessType]);

  // Handler for when a shop is selected
  const handleShopSelect = (shop: IShopifyStore) => {
    setSelectedShop(shop);
    dispatch(APPConfigAction.setSelectedShopifyShopID(shop._id));

    setIsShopDropdownVisible(false);
  };

  // Prepare dropdown items for shop selection
  const shopDropdownItems: DropdownItemInterface[] = shopifyStores.map(
    shop => ({
      label: shop.shop,
      onClick: () => handleShopSelect(shop),
      icon: '',
    })
  );
  return (
    <div
      className={`inline-flex h-full w-16 flex-col items-start justify-between gap-8   px-2
      pb-8 pt-5 text-black duration-500  ${isOpen ? 'md:w-56 md:px-4' : ''} `}
    >
      <div className="flex w-full flex-col">
        <a
          id="logo"
          href="/"
          className=" flex w-full items-center justify-center gap-3"
        >
          <img
            className="relative  rounded-full"
            src="/assets/logo/main.svg"
            alt="Launchify Logo"
          />
          {isOpen && (
            <img
              className="relative hidden md:block"
              src="/assets/logo/logo-text.svg"
              alt="Launchify Logo"
            />
          )}
        </a>
        <div className="relative  top-[13px] hidden w-full md:block">
          <div
            id="openclose"
            className="absolute bottom-2 end-0 z-10 ml-auto mr-[-25px]  cursor-pointer 
           justify-end rounded-full
         border bg-white p-2 text-black  "
            onClick={() => setIsOpen(prev => !prev)}
          >
            {isOpen
              ? React.createElement(FaAnglesLeft, { size: 15 })
              : React.createElement(FaAnglesRight, { size: 15 })}
          </div>
        </div>
        {/* Shopify shop dropdown */}
        <div
          id="shopfiy-select"
          className="relative mt-10 inline-block w-full text-left" // added w-full here
        >
          <button
            onClick={() => setIsShopDropdownVisible(prev => !prev)}
            className="inline-flex h-10 w-full items-center gap-4 rounded-[10px] bg-gradient-to-r from-[#efe9ff] via-[#f8ebff] to-[#f3eaff] px-4 py-2" // added w-full here
          >
            <div className="flex items-center">
              <span className="text-sm font-medium text-[#1c1a22]">
                {selectedShop ? selectedShop.shop : 'Select Shop'}
              </span>
            </div>
            <div className="flex h-5 w-5 items-center justify-center gap-2.5 rounded bg-[#6839ee]/20">
              <img
                className="flex h-4 w-4"
                src="/assets/navbar/arrow-down.svg"
                alt="dropdown"
              />
            </div>
          </button>
          {isShopDropdownVisible && (
            <Dropdown
              items={shopDropdownItems}
              dropdownOutsideClickRef={shopDropdownRef}
            />
          )}
        </div>

        <div
          id="top-section"
          style={{ maxHeight: 'calc(100vh - 4rem)', overflowY: 'auto' }}
          className="mt-[56px] flex flex-col items-center justify-start gap-2 self-stretch overflow-x-hidden" // Add `justify-start` to place it at the start
        >
          {(currentBusinessType === EBusinessType.POD
            ? pod_menu
            : dropshipping_menus
          ).map(({ title, href, iconPath }: any, idx: number) => {
            const isActive = currentPath === href;
            return (
              <CustomSideElement
                title={title}
                href={href}
                iconPath={iconPath}
                key={idx}
                isActive={isActive}
                isOpen={isOpen}
              />
            );
          })}
        </div>
      </div>
      <div
        id="bottom-section"
        className="flex flex-col items-center justify-end gap-4 self-stretch"
      >
        {' '}
        <div
          className="flex w-full flex-col items-start justify-start gap-2.5"
          id="helper-section"
        >
          {extraMenus.map(
            ({ title, href, icon, iconPath }: any, idx: number) => {
              const isActive = currentPath === href;
              return (
                <CustomSideElement
                  title={title}
                  href={href}
                  iconPath={iconPath}
                  key={idx}
                  isActive={isActive}
                  isOpen={isOpen}
                  isActiveBgColor="[#b349ed]/[0.08]"
                  isActiveTextColor="#9b17e4"
                />
              );
            }
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
